// src/lib/lookup.ts
import { doc, getDoc, query, where, getDocs, collection, orderBy } from "firebase/firestore";
import { db } from "../firebase";

export type LookupDisposition = "mapped" | "ignore" | "missing";

type LookupDoc = {
  id: string;
  productId: string | null;
  ignoreProduct: boolean;
  active: boolean;
  isWildcard: boolean;
  wildcardPrefix: string | null;
};

const productCache = new Map<string, string | null>();
const dispositionCache = new Map<string, { disposition: LookupDisposition; productId?: string }>();
const lookupDocCache = new Map<string, LookupDoc | null>();
let wildcardDocs: LookupDoc[] | null = null;
let wildcardDocsPromise: Promise<LookupDoc[]> | null = null;

export function clearLookupCache(): void {
  productCache.clear();
  dispositionCache.clear();
  lookupDocCache.clear();
  wildcardDocs = null;
  wildcardDocsPromise = null;
}

function normalizeProductId(raw: any): string | null {
  if (raw == null) return null;
  const text = String(raw).trim();
  if (!text) return null;
  if (text.toUpperCase() === "NULL") return null;
  return text;
}

function normalizeLookupKey(raw: any): string {
  const text = String(raw ?? "").trim();
  if (!text) return "";
  return text.replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, "-");
}

function deriveWildcardPrefix(id: string, data: any): string | null {
  const explicit = typeof data?.wildcardPrefix === "string" ? data.wildcardPrefix.trim() : "";
  if (explicit) return explicit;
  if (typeof data?.wildcardPrefix === "number") {
    const numPrefix = String(data.wildcardPrefix).trim();
    if (numPrefix) return numPrefix;
  }
  const fromId = id.endsWith("*") ? id.slice(0, -1) : "";
  if (fromId) return fromId;
  if (data?.isWildcard === true && typeof data?.code === "string" && data.code.endsWith("*")) {
    const fromCode = data.code.slice(0, -1).trim();
    if (fromCode) return fromCode;
  }
  return null;
}

function mapLookupDoc(id: string, data: any): LookupDoc {
  const prefix = deriveWildcardPrefix(id, data);
  const isWildcard = data?.isWildcard === true || !!prefix;
  return {
    id,
    productId: normalizeProductId(data?.productId),
    ignoreProduct: data?.ignoreProduct === true,
    active: data?.active === false ? false : true,
    isWildcard,
    wildcardPrefix: prefix,
  };
}

async function fetchLookupDoc(code: string): Promise<LookupDoc | null> {
  if (lookupDocCache.has(code)) return lookupDocCache.get(code) ?? null;
  const snap = await getDoc(doc(db, "lookups", code));
  if (!snap.exists()) {
    lookupDocCache.set(code, null);
    return null;
  }
  const mapped = mapLookupDoc(code, snap.data());
  lookupDocCache.set(code, mapped);
  return mapped;
}

async function loadWildcardDocs(): Promise<LookupDoc[]> {
  if (wildcardDocs) return wildcardDocs;
  if (!wildcardDocsPromise) {
    wildcardDocsPromise = (async () => {
      const docs: LookupDoc[] = [];
      const seen = new Set<string>();
      const baseSnap = await getDocs(query(collection(db, "lookups"), where("isWildcard", "==", true)));
      baseSnap.forEach((ds) => {
        const mapped = mapLookupDoc(ds.id, ds.data());
        docs.push(mapped);
        seen.add(mapped.id);
      });
      const fallbackSnap = await getDocs(
        query(
          collection(db, "lookups"),
          orderBy("wildcardPrefix"),
          where("wildcardPrefix", ">=", ""),
          where("wildcardPrefix", "<=", "\uf8ff")
        )
      );
      fallbackSnap.forEach((ds) => {
        if (seen.has(ds.id)) return;
        const mapped = mapLookupDoc(ds.id, ds.data());
        docs.push(mapped);
        seen.add(mapped.id);
      });
      docs.forEach((docItem) => lookupDocCache.set(docItem.id, docItem));
      wildcardDocs = docs;
      return docs;
    })().finally(() => {
      wildcardDocsPromise = null;
    });
  }
  return wildcardDocsPromise;
}

function isDocActive(doc: LookupDoc | null): doc is LookupDoc {
  return !!doc && doc.active !== false;
}

function dispositionFromDoc(doc: LookupDoc | null): { disposition: LookupDisposition; productId?: string } {
  if (!doc || doc.active === false) return { disposition: "missing" };
  if (doc.ignoreProduct || !doc.productId) return { disposition: "ignore" };
  return { disposition: "mapped", productId: doc.productId };
}

async function findLookupDocForCode(code: string): Promise<LookupDoc | null> {
  const exact = await fetchLookupDoc(code);
  if (isDocActive(exact)) return exact;

  const wildcardList = await loadWildcardDocs();
  if (!wildcardList.length) return null;

  let best: LookupDoc | null = null;
  let longest = -1;
  for (const docItem of wildcardList) {
    const prefix = docItem.wildcardPrefix;
    if (!prefix || docItem.active === false) continue;
    if (code.startsWith(prefix) && prefix.length > longest) {
      best = docItem;
      longest = prefix.length;
    }
  }
  return best;
}

/**
 * Inspect the lookup entry for a given code and classify the outcome.
 * - "mapped": lookup exists and productId is a valid non-NULL value
 * - "ignore": lookup exists but productId is explicitly NULL or the record is flagged as ignore
 * - "missing": no lookup entry exists
 */
export async function getLookupDisposition(
  code: string
): Promise<{ disposition: LookupDisposition; productId?: string }> {
  const key = normalizeLookupKey(code);
  if (!key) return { disposition: "missing" };

  if (dispositionCache.has(key)) return dispositionCache.get(key)!;

  const doc = await findLookupDocForCode(key);
  const result = dispositionFromDoc(doc);
  dispositionCache.set(key, result);
  return result;
}

/**
 * Given a raw SKU from ShipStation (e.g. "2125"), return the productId
 */
export async function resolveSkuToProductId(sku: string): Promise<string | null> {
  if (!sku) return null;
  const key = normalizeLookupKey(sku);
  if (productCache.has(key)) return productCache.get(key)!;
  return resolveAnyCodeToProductId(key);
}

/**
 * More general: given any scanned code (SKU, UPC, internal code, etc),
 * return the productId if there's an active lookup entry.
 */
export async function resolveAnyCodeToProductId(code: string): Promise<string | null> {
  if (!code) return null;
  const key = normalizeLookupKey(code);
  if (productCache.has(key)) return productCache.get(key)!;

  const disp = await getLookupDisposition(key);
  if (disp.disposition === "mapped" && disp.productId) {
    productCache.set(key, disp.productId);
    return disp.productId;
  }
  if (disp.disposition === "ignore") {
    productCache.set(key, null);
    return null;
  }

  const q = query(collection(db, "lookups"), where("code", "==", key), where("active", "==", true));
  const res = await getDocs(q);
  if (!res.empty) {
    const docSnap = res.docs[0];
    const docData = mapLookupDoc(docSnap.id, docSnap.data());
    lookupDocCache.set(docData.id, docData);
    const derived = dispositionFromDoc(docData);
    dispositionCache.set(key, derived);
    if (derived.disposition === "mapped" && derived.productId) {
      productCache.set(key, derived.productId);
      return derived.productId;
    }
  }

  productCache.set(key, null);
  return null;
}
